// TODO: Maybe there is a better name

import { WorkBook, WorkSheet, utils } from "xlsx";
// TODO: Move types from DataContext to this file
import {
    Data,
    ExcelJsonSheet,
    ExcelJsonRow,
    Filter,
    InvestmentDataType,
    RawData,
    Summary,
    SingleTableData,
    exampleData,
    GhgObjectData,
    EmploymentObjectData,
    ValueAddedObjectData,
    GeneralObjectData,
    AttributionObjectData,
    SummaryWithTotals,
    ObjectData,
    OverviewObjectData,
    AttributionFilterOption,
} from "../contexts/DataContext";

import { sheetNames } from "./sheetConstants";

import * as columns from "../utils/columnConstants";
import { SubIndicatorsEmission, SubIndicatorsEmployment, SubIndicatorsVA } from "../utils/subIndicators";
import { SubScope } from "../utils/subScopes";
import { Scope } from "./scopes";

export const processXlsx = (xlsx: WorkBook): RawData | null => {
    return {
        employment: processSheet(xlsx.Sheets[sheetNames.Employment]),
        ghg: processSheet(xlsx.Sheets[sheetNames.GHG]),
        valueAdded: processSheet(xlsx.Sheets[sheetNames.ValueAdded]),
        general: processSheet(xlsx.Sheets[sheetNames.General]),
        attribution: processSheet(xlsx.Sheets[sheetNames.Attribution]),
    };
};

const processRow = (row: { [key: string]: any }): ExcelJsonRow => {
    // for some reason some columns get a leading and trailing space added, so trim to prevent mismatching
    const trimmedKeysRow = Object.keys(row).reduce((acc: { [key: string]: any }, curr: string) => {
        // in the new object use the trimmed key, to read value use the non-trimed key
        acc[curr.trim()] = row[curr];
        return acc;
    }, {});
    const newRow = { ...trimmedKeysRow };
    for (const col of columns.Strings) {
        if (typeof trimmedKeysRow[col] === "string") {
            newRow[col] = trimmedKeysRow[col];
        }
        // also allow converting numbers to strings here
        else if (typeof trimmedKeysRow[col] === "number") {
            newRow[col] = "" + trimmedKeysRow[col];
        } else {
            newRow[col] = "";
        }
    }
    for (const col of columns.Numeric) {
        newRow[col] = typeof trimmedKeysRow[col] === "number" ? trimmedKeysRow[col] : undefined;
    }
    return newRow as ExcelJsonRow;
};

// Rows delivered by the backend's dashboard.json ({headers, rows} arrays): map to keyed objects
// and normalize through the same processRow as the Excel path. Null/undefined = missing.
export const processJsonSheet = (sheet: { headers: string[]; rows: any[][] } | undefined): ExcelJsonSheet => {
    if (!sheet || !Array.isArray(sheet.headers) || !Array.isArray(sheet.rows)) {
        return [];
    }
    return sheet.rows.map((r) => {
        const obj: { [key: string]: any } = {};
        sheet.headers.forEach((h, i) => {
            const v = r[i];
            if (v !== null && v !== undefined) {
                obj[h] = v;
            }
        });
        return processRow(obj);
    });
};

export const processSheet = (sheet: WorkSheet): ExcelJsonSheet => {
    const rawJsonSheet = utils.sheet_to_json<{ [key: string]: any }>(sheet);
    const jsonSheet: ExcelJsonSheet = rawJsonSheet.map(processRow);
    return jsonSheet;
};

// A row belongs in the Attributed view once it has a real share to multiply by -- NOT once it
// already carries a pre-computed "Attribution - Outstanding amount" dollar figure (Ceren/supervisor,
// 2026-09-21). Those are two different things: a bare Sector Exposure leg (no base IC/FI row at all)
// now correctly gets outstandingShare = 1 so it counts 1:1 in
// Attributed mode -- but it deliberately never gets an "Attribution - Outstanding amount" dollar
// value, because that column only exists for rows that matched a real Attribution-sheet entry (see
// old_jim_post_processing.py). Gating on outstandingAmount silently dropped every bare-SE client out
// of the Attributed view's client list entirely (general/ghg/employment/valueAdded all filtered the
// same way), even though the Table sheet already showed them 1:1 -- gating on share instead makes the
// dashboard's Attributed view match the Table: any row with a real share (explicit or now-1:1 bare
// SE) is included; a row whose share is still genuinely missing (Guard 1 -- absent, not an entered
// zero) stays excluded, exactly as before.
const hasAttributionShare = (row: { outstandingShare?: number; committedShare?: number }): boolean =>
    row.outstandingShare !== undefined || row.committedShare !== undefined;

export const filterData = (objectData: ObjectData, filter: Filter): ObjectData => {
    let filteredAttribution = objectData.attribution.filter((row) => row.year && filter.years?.includes(row.year));
    let filteredEmployment = objectData.employment.filter((row) => row.year && filter.years?.includes(row.year));
    let filteredGeneral = objectData.general.filter((row) => row.year && filter.years?.includes(row.year));
    let filteredGhg = objectData.ghg.filter((row) => row.year && filter.years?.includes(row.year));
    let filteredValueAdded = objectData.valueAdded.filter((row) => row.year && filter.years?.includes(row.year));

    // No need to filter or multiply on attribution === noFilter
    // "Committed" was removed as a selectable attribution filter (Ceren/FH/Rebekah 2026-09-18) --
    // "outstanding" is the only remaining share-based filter.

    if (filter.attribution === "outstanding") {
        //filteredAttribution = filteredAttribution.filter(hasAttributionShare);
        // Cast to the *Internal types here: every object in these arrays was actually built by
        // createEmploymentObjects/createGhgObjects/createValueAddedObjects (via createObjectData),
        // so it genuinely carries sourceRows -- the public ObjectData/EmploymentObjectData etc.
        // types (DataContext.tsx) just don't expose that internal-only field. See the comment on
        // GhgObjectDataInternal. (Ceren/Claude, 2026-09-24)
        filteredEmployment = filteredEmployment
            .filter(hasAttributionShare)
            .map((obj) => multiplyEmploymentWithShare(obj as EmploymentObjectDataInternal, filter.attribution));
        // No need to multiply general data with share
        filteredGeneral = filteredGeneral.filter(hasAttributionShare);
        filteredGhg = filteredGhg.filter(hasAttributionShare).map((obj) => multiplyGhgWithShare(obj as GhgObjectDataInternal, filter.attribution));
        filteredValueAdded = filteredValueAdded
            .filter(hasAttributionShare)
            .map((obj) => multiplyValueAddedWithShare(obj as ValueAddedObjectDataInternal, filter.attribution));
    }

    return {
        attribution: filteredAttribution,
        general: filteredGeneral,
        ghg: filteredGhg,
        employment: filteredEmployment,
        valueAdded: filteredValueAdded,
    };
};

type ParsedRow = {
    id: string;
    client: string;
    investee: string;
    country: string;
    econAct: string;
    scope: Scope;
    subScope: SubScope;
    subIndicator: SubIndicatorsEmission | SubIndicatorsEmployment | SubIndicatorsVA;
    year: number | undefined;
    total: number | undefined;
    revenue: number | undefined;
    outstandingAmount: number | undefined;
    outstandingShare: number | undefined;
    committedShare: number | undefined;
};

/*
INvestmentdata = General ttab, just look at unique and sums of TOTAL, attribution sheet for INVESTMENT (exclude investees)
GHG scope 1+2 exclude 'FINANCE ENABLING' from sums! for scope3 include all finance enabling!

Overview page, Dark Medium colors only

Employment Summary:
First select TOTAL
Then split on Scope (Finance, Power, Backward)
ONly backward (Temp + perm) should be split on SubScope down into Suply/Direct/Induced

Total outstandingAmount = Employment sheet get SubIND == Total and SubScope=Dir and
Total outstandingAmount in general: NO investee take the 'Attribution - outstandingAmount amount' only for clients / FI

Tabel filter is just one of three: [Client, Country, EconAct]

In the table intensities are xtotal / total outstandingAmount

Empty or infinity should display n/a

Filtering on Attribution should multiply the total values by either committed or outstandingAmount share

*/

export const sumNumbersWithUndefined = (numbers: (number | undefined)[]): number | undefined => {
    const summableNumbers = numbers.filter((num) => num !== undefined) as number[];
    return summableNumbers.length > 0 ? summableNumbers.reduce((partialSum, a) => partialSum + a, 0) : undefined;
};

const perMilion = (num: number): number => {
    return num * 1_000_000;
};

// Same Guard-1 issue as applyShare below, one level up: `value && amount ? perMilion(value / amount) :
// undefined` treats a real zero value (or a real zero investment amount) as missing, same as JS
// falsiness treats any other 0. A genuinely-zero numerator with a real denominator IS a real intensity
// of 0, not n/a -- e.g. Client 1.a's real Scope 1/2 total of 0 was showing n/a instead of 0/mUSD.
// (Ceren/Claude, 2026-09-23)
const intensityOf = (value: number | undefined, amount: number | undefined): number | undefined =>
    value !== undefined && amount !== undefined ? perMilion(value / amount) : undefined;

// The loan or investment amount of a row: the "Total investment" column and the denominator of every
// per-mUSD intensity. Mirrors the output Table's loan column fallback: "Attribution - Outstanding
// amount", else "Attribution - Committed amount" (a committed-only client), else the raw "Outstanding
// amount". `??` not `||`: an entered 0 stays 0 and does not fall through. Which rows count a loan is
// decided by the sums below: rows labelled Finance Enabling and investee rows never do (as in 4.2).
//
// The raw "Outstanding amount" is filled on Sector exposure rows only. It is what a client entered on
// Sector exposure alone counts: such a client cannot have an Attribution row (the input checks stop the
// run), so it counts 1:1 and its lines' amounts add up to its investment. Its lines are labelled Finance
// Enabling by default (no loan, as in 4.2); marked "Own client" they are not, and they count these amounts.
const loanAmount = (row: ExcelJsonRow): number | undefined =>
    row[columns.AttributionOutstanding] ?? row[columns.AttributionCommitted] ?? row[columns.OutstandingAmount];

const getParsedRows = (rows: ExcelJsonRow[]): ParsedRow[] => {
    // unique id based on Client, Investee, FiscalYear, Country, EconAct
    const parsedRows = rows.map((row) => ({
        id: [row[columns.Client], row[columns.Investee], row[columns.FiscalYear], row[columns.Country], row[columns.EconAct]].join("_"),
        client: row[columns.Client],
        investee: row[columns.Investee],
        country: row[columns.Country],
        econAct: row[columns.EconAct],
        scope: row[columns.Scope] as Scope, // perhaps find a more clean way to check this?
        subScope: row[columns.SubScope] as SubScope,
        subIndicator: row[columns.SubIndicator] as SubIndicatorsEmission | SubIndicatorsEmployment | SubIndicatorsVA,
        year: row[columns.FiscalYear],
        total: row[columns.Total],
        revenue: row[columns.Revenue],
        outstandingAmount: loanAmount(row),
        outstandingShare: row[columns.AttributionShareOutstanding],
        committedShare: row[columns.AttributionShareCommitted],
    }));
    return parsedRows;
};

// create objects from excel rows based on 'unique id'

// PERF TODO (O(N^2) load freeze): every create*Objects below does
// `uniqueIds.map(id => rows.filter(r => r.id === id))` — a full rescan per id. Measured load
// ~0.5s @ 30k rows but ~4s @ 100k / ~9s @ 150k on a fast desktop (2-4x worse on a laptop). This
// is the ONLY reason the dashboard row cap (JIM_DASHBOARD_MAX_ROWS) is held at 30k. Fix: bucket
// rows by id once into a Map (single pass -> linear), then build each object from its bucket;
// after that the cap can be raised. Interactions (filterData) are already ~free.
export const createObjectData = (rawData: RawData | null): ObjectData | null => {
    if (rawData) {
        const attributionRows = getParsedRows(rawData.attribution);
        const employmentRows = getParsedRows(rawData.employment);
        const generalRows = getParsedRows(rawData.general);
        const ghgRows = getParsedRows(rawData.ghg);
        const valueAddedRows = getParsedRows(rawData.valueAdded);
        return {
            attribution: createAttributionObjects(attributionRows),
            employment: createEmploymentObjects(employmentRows),
            general: createGeneralObjects(generalRows, ghgRows),
            ghg: createGhgObjects(ghgRows),
            valueAdded: createValueAddedObjects(valueAddedRows),
        };
    }
    return null;
};

// GhgObjectData plus the source rows it was built from -- kept internal to this file (not part of
// the public DataContext type) purely so multiplyGhgWithShare can re-find each field's own row and
// use THAT row's share, instead of a single share picked once for the whole id+scope. See the
// comment on multiplyGhgWithShare for why that matters. (Ceren/Claude, 2026-09-24)
type GhgObjectDataInternal = GhgObjectData & { sourceRows: ParsedRow[] };

const getGhgObjectForScope = (id: string, scope: Scope, rows: ParsedRow[]): GhgObjectDataInternal | undefined => {
    // stop if rows is empty
    if (rows.length === 0) {
        return undefined;
    }
    // available in each row, so pick the first
    const client = rows[0]?.client;
    const country = rows[0]?.country;
    const econAct = rows[0]?.econAct;
    const investee = rows[0]?.investee;

    const year = rows[0]?.year;
    const outstandingAmount = rows[0]?.outstandingAmount;
    const outstandingShare = rows[0]?.outstandingShare;
    const committedShare = rows[0]?.committedShare;

    // row specific, so find correct row
    const s1co2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope1CO2)?.total;
    const s1nonco2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope1nonCO2)?.total;

    const s2co2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope2CO2)?.total;
    const s2nonco2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope2nonCO2)?.total;

    const s3impco2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope3CO2imports)?.total;
    const s3impnonco2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope3nonCO2imports)?.total;
    const s3locco2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope3CO2local)?.total;
    const s3locnonco2 = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope3nonCO2local)?.total;

    const s3locco2_downstream = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope3CO2localdown)?.total;
    const s3locnonco2_downstream = rows.find((row) => row.subIndicator === SubIndicatorsEmission.Scope3nonCO2localdown)?.total;

    return {
        id: id,
        client: client,
        country: country,
        econAct: econAct,
        investee: investee,
        scope: scope,
        year: year,
        outstandingAmount: outstandingAmount,
        outstandingShare: outstandingShare,
        committedShare: committedShare,
        scope1: { co2: s1co2, nonco2: s1nonco2, total: sumNumbersWithUndefined([s1co2, s1nonco2]) },
        scope2: { co2: s2co2, nonco2: s2nonco2, total: sumNumbersWithUndefined([s2co2, s2nonco2]) },
        scope3: {
            co2: sumNumbersWithUndefined([s3locco2, s3impco2]),
            nonco2: sumNumbersWithUndefined([s3locnonco2, s3impnonco2]),
            total: sumNumbersWithUndefined([s3locco2, s3impco2, s3locnonco2, s3impnonco2]),
        },
        scope3_downstream: {
            co2: s3locco2_downstream,
            nonco2: s3locnonco2_downstream,
            total: sumNumbersWithUndefined([s3locco2_downstream, s3locnonco2_downstream]),
        },
        sourceRows: rows,
    };
};

const createGhgObjects = (rows: ParsedRow[]): GhgObjectDataInternal[] => {
    // convert multiple rows per client to a single object that has attributes
    const uniqueIds = Array.from(new Set(rows.map((row) => row.id)));
    const combinedObjects = uniqueIds.map((id) => {
        // There is never power enabling in ghg
        const backTemporaryRows = rows.filter((row) => row.id === id && row.scope === Scope.BackwardTemporary);
        const backPermanentRows = rows.filter((row) => row.id === id && row.scope === Scope.BackwardPermanent);
        const financeRows = rows.filter((row) => row.id === id && row.scope === Scope.FinanceEnabling);

        const backTempObj = getGhgObjectForScope(id, Scope.BackwardTemporary, backTemporaryRows);
        const backPermObj = getGhgObjectForScope(id, Scope.BackwardPermanent, backPermanentRows);
        const financeObj = getGhgObjectForScope(id, Scope.FinanceEnabling, financeRows);

        const result = [];
        if (backTempObj) {
            result.push(backTempObj);
        }
        if (backPermObj) {
            result.push(backPermObj);
        }
        if (financeObj) {
            result.push(financeObj);
        }
        return result;
    });
    const uniqueObjects = combinedObjects.reduce((prev, curr) => prev.concat(curr), []);
    return uniqueObjects;
};

// A component is genuinely missing only when it's `undefined` -- a real, entered zero must stay
// zero after multiplying by share, not vanish into `undefined` the way `value && share` treats it
// (0 is falsy in JS, same class of bug as Guard 1 elsewhere -- e.g. Client 1.a's real Scope 1/2
// total of 0 was showing n/a instead of 0 in Attributed mode). (Ceren/Claude, 2026-09-23)
const applyShare = (value: number | undefined, share: number | undefined): number | undefined =>
    value !== undefined && share !== undefined ? value * share : undefined;

// Guard 1, one level deeper than applyShare: each Total must be multiplied by the share recorded
// on THAT SAME source row, never a single share borrowed from whichever row happened to be rows[0]
// for the id+scope. A client can legitimately carry a DIFFERENT attribution share per sub-scope leg
// -- e.g. Client 1.a's 2026-09-24 test file has Direct share 0.02 (real total 0) vs Supply
// Chain/Induced share 0.0004 (real non-zero totals). Multiplying the whole object by one shared
// value silently misattributed the Supply Chain/Induced figures by the Direct leg's share instead
// of their own -- 50x too high on Value Added and Employment for that client, though it happened
// not to show up in GHG or Jobs' own totals in earlier files only because rows[0] there happened to
// be the row that mattered. Re-finding each field's own row (same predicate used to extract it) and
// using THAT row's share fixes all three consistently. (Ceren/Claude, 2026-09-24)
const shareForRow = (row: ParsedRow | undefined): number | undefined =>
    row === undefined ? undefined : row.outstandingShare !== undefined ? row.outstandingShare : row.committedShare;

// Employment / Value Added: the Total of one sub-indicator, summed over EVERY matching row instead of
// read off the first one, as the output Table's SUMIFS does. With one row per sub-scope and
// sub-indicator (the usual case) this is that row's Total; if a client ever carries two such rows,
// `.find` would silently drop the second. undefined only when no matching row has a Total.
const sumTotals = (rows: ParsedRow[], indicator: SubIndicatorsEmployment | SubIndicatorsVA): number | undefined =>
    sumNumbersWithUndefined(rows.filter((row) => row.subIndicator === indicator).map((row) => row.total));

// Attributed counterpart of sumTotals: each matching row's Total times THAT row's own share, then
// summed. A row with a Total but no share makes the sum unknown -- never a partial sum that looks
// complete.
const sumAttributed = (rows: ParsedRow[], indicator: SubIndicatorsEmployment | SubIndicatorsVA): number | undefined => {
    const values = rows
        .filter((row) => row.subIndicator === indicator && row.total !== undefined)
        .map((row) => applyShare(row.total, shareForRow(row)));
    return values.includes(undefined) ? undefined : sumNumbersWithUndefined(values);
};

const multiplyGhgWithShare = (obj: GhgObjectDataInternal, attributionFilter: AttributionFilterOption | null): GhgObjectDataInternal => {
    if (attributionFilter === null || attributionFilter === "noFilter") {
        return obj;
    }
    const rows = obj.sourceRows;
    const multRow = (indicator: SubIndicatorsEmission): number | undefined => {
        const row = rows.find((r) => r.subIndicator === indicator);
        return applyShare(row?.total, shareForRow(row));
    };

    const s1co2 = multRow(SubIndicatorsEmission.Scope1CO2);
    const s1nonco2 = multRow(SubIndicatorsEmission.Scope1nonCO2);

    const s2co2 = multRow(SubIndicatorsEmission.Scope2CO2);
    const s2nonco2 = multRow(SubIndicatorsEmission.Scope2nonCO2);

    const s3impco2 = multRow(SubIndicatorsEmission.Scope3CO2imports);
    const s3impnonco2 = multRow(SubIndicatorsEmission.Scope3nonCO2imports);
    const s3locco2 = multRow(SubIndicatorsEmission.Scope3CO2local);
    const s3locnonco2 = multRow(SubIndicatorsEmission.Scope3nonCO2local);
    const s3co2 = sumNumbersWithUndefined([s3impco2, s3locco2]);
    const s3nonco2 = sumNumbersWithUndefined([s3impnonco2, s3locnonco2]);

    const s3co2_downstream = multRow(SubIndicatorsEmission.Scope3CO2localdown);
    const s3nonco2_downstream = multRow(SubIndicatorsEmission.Scope3nonCO2localdown);

    return {
        ...obj,
        scope1: { co2: s1co2, nonco2: s1nonco2, total: sumNumbersWithUndefined([s1co2, s1nonco2]) },
        scope2: { co2: s2co2, nonco2: s2nonco2, total: sumNumbersWithUndefined([s2co2, s2nonco2]) },
        scope3: { co2: s3co2, nonco2: s3nonco2, total: sumNumbersWithUndefined([s3co2, s3nonco2]) },
        scope3_downstream: { co2: s3co2_downstream, nonco2: s3nonco2_downstream, total: sumNumbersWithUndefined([s3co2_downstream, s3nonco2_downstream]) },
    };
};

// See the comment on GhgObjectDataInternal above -- same reasoning, kept internal to this file.
type EmploymentObjectDataInternal = EmploymentObjectData & { sourceRows: ParsedRow[] };

const getEmploymentObjectForScope = (id: string, scope: Scope, rows: ParsedRow[]): EmploymentObjectDataInternal | undefined => {
    // stop if rows is empty
    if (rows.length === 0) {
        return undefined;
    }
    // available in each row, so pick the first
    const client = rows[0]?.client;
    const country = rows[0]?.country;
    const econAct = rows[0]?.econAct;
    const investee = rows[0]?.investee;

    const year = rows[0]?.year;
    const outstandingAmount = rows[0]?.outstandingAmount;
    const outstandingShare = rows[0]?.outstandingShare;
    const committedShare = rows[0]?.committedShare;

    // row specific, so sum the matching rows (see sumTotals)
    const directRows = rows.filter((row) => row.subScope === SubScope.Direct);
    const supplyRows = rows.filter((row) => row.subScope === SubScope.SupplyChain);
    const inducedRows = rows.filter((row) => row.subScope === SubScope.Induced);
    const powerEnablingRows = rows.filter((row) => row.subScope === SubScope.PowerEnabling);

    const directTotal = sumTotals(directRows, SubIndicatorsEmployment.Total);
    const directFemale = sumTotals(directRows, SubIndicatorsEmployment.Female);
    const directFormal = sumTotals(directRows, SubIndicatorsEmployment.Formal);

    const supplyTotal = sumTotals(supplyRows, SubIndicatorsEmployment.Total);
    const supplyFemale = sumTotals(supplyRows, SubIndicatorsEmployment.Female);
    const supplyFormal = sumTotals(supplyRows, SubIndicatorsEmployment.Formal);

    const inducedTotal = sumTotals(inducedRows, SubIndicatorsEmployment.Total);
    const inducedFemale = sumTotals(inducedRows, SubIndicatorsEmployment.Female);
    const inducedFormal = sumTotals(inducedRows, SubIndicatorsEmployment.Formal);

    const powerTotal = sumTotals(powerEnablingRows, SubIndicatorsEmployment.Total);
    const powerFemale = sumTotals(powerEnablingRows, SubIndicatorsEmployment.Female);
    const powerFormal = sumTotals(powerEnablingRows, SubIndicatorsEmployment.Formal);

    return {
        id: id,
        client: client,
        country: country,
        econAct: econAct,
        investee: investee,
        scope: scope,
        year: year,
        outstandingAmount: outstandingAmount,
        outstandingShare: outstandingShare,
        committedShare: committedShare,
        direct: { total: directTotal, female: directFemale, formal: directFormal },
        supply: { total: supplyTotal, female: supplyFemale, formal: supplyFormal },
        induced: { total: inducedTotal, female: inducedFemale, formal: inducedFormal },
        power: { total: powerTotal, female: powerFemale, formal: powerFormal },
        combined: {
            total: sumNumbersWithUndefined([directTotal, supplyTotal, inducedTotal, powerTotal]),
            female: sumNumbersWithUndefined([directFemale, supplyFemale, inducedFemale, powerFemale]),
            formal: sumNumbersWithUndefined([directFormal, supplyFormal, inducedFormal, powerFormal]),
        },
        sourceRows: rows,
    };
};

const createEmploymentObjects = (rows: ParsedRow[]): EmploymentObjectDataInternal[] => {
    // convert multiple rows per client to a scope based objects that have attributes
    const uniqueIds = Array.from(new Set(rows.map((row) => row.id)));
    const combinedObjects = uniqueIds.map((id) => {
        const backTemporaryRows = rows.filter((row) => row.id === id && row.scope === Scope.BackwardTemporary);
        const backPermanentRows = rows.filter((row) => row.id === id && row.scope === Scope.BackwardPermanent);
        const financeRows = rows.filter((row) => row.id === id && row.scope === Scope.FinanceEnabling);
        const powerRows = rows.filter((row) => row.id === id && row.scope === Scope.PowerEnabling);

        const backTempObj = getEmploymentObjectForScope(id, Scope.BackwardTemporary, backTemporaryRows);
        const backPermObj = getEmploymentObjectForScope(id, Scope.BackwardPermanent, backPermanentRows);
        const financeObj = getEmploymentObjectForScope(id, Scope.FinanceEnabling, financeRows);
        const powerObj = getEmploymentObjectForScope(id, Scope.PowerEnabling, powerRows);

        const result = [];
        if (backTempObj) {
            result.push(backTempObj);
        }
        if (backPermObj) {
            result.push(backPermObj);
        }
        if (financeObj) {
            result.push(financeObj);
        }
        if (powerObj) {
            result.push(powerObj);
        }
        return result;
    });
    const uniqueObjects = combinedObjects.reduce((prev, curr) => prev.concat(curr), []);
    return uniqueObjects;
};

const multiplyEmploymentWithShare = (obj: EmploymentObjectDataInternal, attributionFilter: AttributionFilterOption | null): EmploymentObjectDataInternal => {
    if (attributionFilter === null || attributionFilter === "noFilter") {
        return obj;
    }
    // Per-row share (see shareForRow / the comment on multiplyGhgWithShare above): Direct, Supply
    // Chain, Induced and Power Enabling can each carry their OWN attribution share on their own
    // source rows -- multiplying the whole object by one shared value (as this used to) silently
    // misattributed whichever sub-scope's real figures happened not to be rows[0] by the WRONG
    // sub-scope's share. Re-filter by sub-scope and take each field's own rows exactly as
    // getEmploymentObjectForScope did, each multiplied by its OWN share. (Ceren/Claude, 2026-09-24)
    const rows = obj.sourceRows;
    const multFor = (subScope: SubScope, indicator: SubIndicatorsEmployment): number | undefined =>
        sumAttributed(rows.filter((r) => r.subScope === subScope), indicator);

    const directTotal = multFor(SubScope.Direct, SubIndicatorsEmployment.Total);
    const directFemale = multFor(SubScope.Direct, SubIndicatorsEmployment.Female);
    const directFormal = multFor(SubScope.Direct, SubIndicatorsEmployment.Formal);

    const supplyTotal = multFor(SubScope.SupplyChain, SubIndicatorsEmployment.Total);
    const supplyFemale = multFor(SubScope.SupplyChain, SubIndicatorsEmployment.Female);
    const supplyFormal = multFor(SubScope.SupplyChain, SubIndicatorsEmployment.Formal);

    const inducedTotal = multFor(SubScope.Induced, SubIndicatorsEmployment.Total);
    const inducedFemale = multFor(SubScope.Induced, SubIndicatorsEmployment.Female);
    const inducedFormal = multFor(SubScope.Induced, SubIndicatorsEmployment.Formal);

    const powerTotal = multFor(SubScope.PowerEnabling, SubIndicatorsEmployment.Total);
    const powerFemale = multFor(SubScope.PowerEnabling, SubIndicatorsEmployment.Female);
    const powerFormal = multFor(SubScope.PowerEnabling, SubIndicatorsEmployment.Formal);

    return {
        ...obj,
        direct: { total: directTotal, female: directFemale, formal: directFormal },
        supply: { total: supplyTotal, female: supplyFemale, formal: supplyFormal },
        induced: { total: inducedTotal, female: inducedFemale, formal: inducedFormal },
        // `power:` was missing here before -- obj.power silently kept its RAW, un-multiplied values
        // (the "Power enabling" row in the Jobs breakdown table reads obj.power directly, not
        // .combined), even though combined.total already correctly included the freshly-computed
        // powerTotal. Invisible in every file so far because none had real Power Enabling employment
        // data yet. (Ceren/Claude, 2026-09-24)
        power: { total: powerTotal, female: powerFemale, formal: powerFormal },
        combined: {
            total: sumNumbersWithUndefined([directTotal, supplyTotal, inducedTotal, powerTotal]),
            female: sumNumbersWithUndefined([directFemale, supplyFemale, inducedFemale, powerFemale]),
            formal: sumNumbersWithUndefined([directFormal, supplyFormal, inducedFormal, powerFormal]),
        },
    };
};

const createAttributionObjects = (rows: ParsedRow[]): AttributionObjectData[] => {
    // convert multiple rows per client to a single object that has attributes
    const uniqueIds = Array.from(new Set(rows.map((row) => row.id)));
    const uniqueObjects = uniqueIds.map((id) => {
        const filtered = rows.filter((row) => row.id === id);

        return {
            id: id,
            client: filtered[0]?.client || "<Unknown>",
            country: filtered[0]?.country || "<Unknown>",
            econAct: filtered[0]?.econAct || "<Unknown>",
            year: filtered[0]?.year,
            outstandingAmount: filtered[0]?.outstandingAmount,
            outstandingShare: filtered[0]?.outstandingShare,
            committedShare: filtered[0]?.committedShare,
        };
    });
    return uniqueObjects;
};

const createGeneralObjects = (generalRows: ParsedRow[], ghgRows: ParsedRow[]): GeneralObjectData[] => {
    // convert multiple rows per client to a single object that has attributes
    const uniqueIds = Array.from(new Set(generalRows.map((row) => row.id)));
    const uniqueObjects = uniqueIds.map((id) => {
        const filtered = generalRows.filter((row) => row.id === id);
        const ghgRow = ghgRows.find((row) => row.id === id);
        return {
            id: id,
            client: filtered[0]?.client || "<Unknown>",
            country: filtered[0]?.country || "<Unknown>",
            econAct: filtered[0]?.econAct || "<Unknown>",
            investee: filtered[0]?.investee,
            scope: filtered[0]?.scope,
            year: filtered[0]?.year,
            revenue: filtered[0]?.revenue,

            outstandingAmount: ghgRow?.outstandingAmount,
            outstandingShare: ghgRow?.outstandingShare,
            committedShare: ghgRow?.committedShare,
        };
    });
    return uniqueObjects;
};

// See the comment on GhgObjectDataInternal above -- same reasoning, kept internal to this file.
type ValueAddedObjectDataInternal = ValueAddedObjectData & { sourceRows: ParsedRow[] };

const getValueAddedObjectForScope = (id: string, scope: Scope, rows: ParsedRow[]): ValueAddedObjectDataInternal | undefined => {
    // stop if rows is empty
    if (rows.length === 0) {
        return undefined;
    }
    // available in each row, so pick the first
    const client = rows[0]?.client;
    const country = rows[0]?.country;
    const econAct = rows[0]?.econAct;
    const investee = rows[0]?.investee;

    const year = rows[0]?.year;
    const outstandingAmount = rows[0]?.outstandingAmount;
    const outstandingShare = rows[0]?.outstandingShare;
    const committedShare = rows[0]?.committedShare;

    // row specific, so sum the matching rows (see sumTotals)
    const directRows = rows.filter((row) => row.subScope === SubScope.Direct);
    const supplyRows = rows.filter((row) => row.subScope === SubScope.SupplyChain);
    const powerRows = rows.filter((row) => row.subScope === SubScope.PowerEnabling);

    const directNetIncome = sumTotals(directRows, SubIndicatorsVA.NetIncome);
    const directWages = sumTotals(directRows, SubIndicatorsVA.Wages);
    const directTaxes = sumTotals(directRows, SubIndicatorsVA.Taxes);

    const supplyNetIncome = sumTotals(supplyRows, SubIndicatorsVA.NetIncome);
    const supplyWages = sumTotals(supplyRows, SubIndicatorsVA.Wages);
    const supplyTaxes = sumTotals(supplyRows, SubIndicatorsVA.Taxes);

    const powerNetIncome = sumTotals(powerRows, SubIndicatorsVA.NetIncome);
    const powerWages = sumTotals(powerRows, SubIndicatorsVA.Wages);
    const powerTaxes = sumTotals(powerRows, SubIndicatorsVA.Taxes);
    return {
        id: id,
        client: client,
        country: country,
        econAct: econAct,
        investee: investee,
        scope: scope,
        year: year,
        outstandingAmount: outstandingAmount,
        outstandingShare: outstandingShare,
        committedShare: committedShare,
        direct: { netIncome: directNetIncome, wages: directWages, taxes: directTaxes },
        supply: { netIncome: supplyNetIncome, wages: supplyWages, taxes: supplyTaxes },
        power: { netIncome: powerNetIncome, wages: powerWages, taxes: powerTaxes },
        combined: {
            netIncome: sumNumbersWithUndefined([directNetIncome, supplyNetIncome, powerNetIncome]),
            wages: sumNumbersWithUndefined([directWages, supplyWages, powerWages]),
            taxes: sumNumbersWithUndefined([directTaxes, supplyTaxes, powerTaxes]),
        },
        sourceRows: rows,
    };
};

const createValueAddedObjects = (rows: ParsedRow[]): ValueAddedObjectDataInternal[] => {
    // convert multiple rows per client to a scope based objects that have attributes
    const uniqueIds = Array.from(new Set(rows.map((row) => row.id)));
    const combinedObjects = uniqueIds.map((id) => {
        const backTemporaryRows = rows.filter((row) => row.id === id && row.scope === Scope.BackwardTemporary);
        const backPermanentRows = rows.filter((row) => row.id === id && row.scope === Scope.BackwardPermanent);
        const financeRows = rows.filter((row) => row.id === id && row.scope === Scope.FinanceEnabling);
        const powerRows = rows.filter((row) => row.id === id && row.scope === Scope.PowerEnabling);

        const backTempObj = getValueAddedObjectForScope(id, Scope.BackwardTemporary, backTemporaryRows);
        const backPermObj = getValueAddedObjectForScope(id, Scope.BackwardPermanent, backPermanentRows);
        const financeObj = getValueAddedObjectForScope(id, Scope.FinanceEnabling, financeRows);
        const powerObj = getValueAddedObjectForScope(id, Scope.PowerEnabling, powerRows);

        const result = [];
        if (backTempObj) {
            result.push(backTempObj);
        }
        if (backPermObj) {
            result.push(backPermObj);
        }
        if (financeObj) {
            result.push(financeObj);
        }
        if (powerObj) {
            result.push(powerObj);
        }
        return result;
    });
    const uniqueObjects = combinedObjects.reduce((prev, curr) => prev.concat(curr), []);
    return uniqueObjects;
};

const multiplyValueAddedWithShare = (obj: ValueAddedObjectDataInternal, attributionFilter: AttributionFilterOption | null): ValueAddedObjectDataInternal => {
    if (attributionFilter === null || attributionFilter === "noFilter") {
        return obj;
    }
    // Per-row share -- see the comment on multiplyGhgWithShare above. Direct, Supply Chain and Power
    // Enabling can each carry their OWN attribution share on their own source rows (e.g. Client 1.a's
    // 2026-09-24 test file: Direct share 0.02 real-zero vs Supply Chain share 0.0004 real non-zero
    // wages/net income/taxes) -- multiplying the whole object by one shared value applied the WRONG
    // sub-scope's share to Supply Chain's real figures (50x too high here). Re-filter by sub-scope and
    // take each field's own rows exactly as getValueAddedObjectForScope did, each multiplied by its OWN
    // share. (Ceren/Claude, 2026-09-24)
    const rows = obj.sourceRows;
    const multFor = (subScope: SubScope, indicator: SubIndicatorsVA): number | undefined =>
        sumAttributed(rows.filter((r) => r.subScope === subScope), indicator);

    const directNetIncome = multFor(SubScope.Direct, SubIndicatorsVA.NetIncome);
    const directWages = multFor(SubScope.Direct, SubIndicatorsVA.Wages);
    const directTaxes = multFor(SubScope.Direct, SubIndicatorsVA.Taxes);

    const supplyNetIncome = multFor(SubScope.SupplyChain, SubIndicatorsVA.NetIncome);
    const supplyWages = multFor(SubScope.SupplyChain, SubIndicatorsVA.Wages);
    const supplyTaxes = multFor(SubScope.SupplyChain, SubIndicatorsVA.Taxes);

    const powerNetIncome = multFor(SubScope.PowerEnabling, SubIndicatorsVA.NetIncome);
    const powerWages = multFor(SubScope.PowerEnabling, SubIndicatorsVA.Wages);
    const powerTaxes = multFor(SubScope.PowerEnabling, SubIndicatorsVA.Taxes);

    return {
        ...obj,
        direct: { netIncome: directNetIncome, wages: directWages, taxes: directTaxes },
        supply: { netIncome: supplyNetIncome, wages: supplyWages, taxes: supplyTaxes },
        power: { netIncome: powerNetIncome, wages: powerWages, taxes: powerTaxes },
        combined: {
            netIncome: sumNumbersWithUndefined([directNetIncome, supplyNetIncome, powerNetIncome]),
            wages: sumNumbersWithUndefined([directWages, supplyWages, powerWages]),
            taxes: sumNumbersWithUndefined([directTaxes, supplyTaxes, powerTaxes]),
        },
    };
};

// create data for big tables at bottom

const makeOverviewObjectForScope = (
    id: string,
    scope: Scope,
    ghg: GhgObjectData[],
    employment: EmploymentObjectData[],
    valueAdded: ValueAddedObjectData[],
): OverviewObjectData | undefined => {
    const ghgObj = ghg.find((row) => row.id === id && row.scope === scope);
    const employmentObj = employment.find((row) => row.id === id && row.scope === scope);
    const valueAddedObj = valueAdded.find((row) => row.id === id && row.scope === scope);
    if (ghgObj || employmentObj || valueAddedObj) {
        return {
            client: ghgObj?.client || employmentObj?.client || valueAddedObj?.client || "<Unknown>",
            country: ghgObj?.country || employmentObj?.country || valueAddedObj?.country || "<Unknown>",
            econAct: ghgObj?.econAct || employmentObj?.econAct || valueAddedObj?.econAct || "<Unknown>",
            investee: ghgObj?.investee || employmentObj?.investee || valueAddedObj?.investee || undefined,
            outstandingAmount: ghgObj?.outstandingAmount || employmentObj?.outstandingAmount || valueAddedObj?.outstandingAmount || undefined,
            ghg: ghgObj,
            employment: employmentObj,
            valueAdded: valueAddedObj,
        };
    }
    return undefined;
};

const createOverviewTableData = (
    ghg: GhgObjectData[],
    employment: EmploymentObjectData[],
    valueAdded: ValueAddedObjectData[],
    attributionFilter: AttributionFilterOption | null,
): SingleTableData => {
    const uniqueIds = Array.from(new Set([...employment.map((obj) => obj.id), ...ghg.map((obj) => obj.id), ...valueAdded.map((obj) => obj.id)]));
    const combinedObjects = uniqueIds.map((id) => {
        const temp = makeOverviewObjectForScope(id, Scope.BackwardTemporary, ghg, employment, valueAdded);
        const perm = makeOverviewObjectForScope(id, Scope.BackwardPermanent, ghg, employment, valueAdded);
        const finance = makeOverviewObjectForScope(id, Scope.FinanceEnabling, ghg, employment, valueAdded);
        const power = makeOverviewObjectForScope(id, Scope.PowerEnabling, ghg, employment, valueAdded);

        const investee = temp?.investee || perm?.investee || finance?.investee || power?.investee || undefined;
        const outstandingAmount = temp?.outstandingAmount || perm?.outstandingAmount || finance?.outstandingAmount || power?.outstandingAmount || undefined;

        // ignore finance enabling scope1+2
        const scope12 = sumNumbersWithUndefined([
            temp?.ghg?.scope1.total,
            temp?.ghg?.scope2.total,
            perm?.ghg?.scope1.total,
            perm?.ghg?.scope2.total,
            power?.ghg?.scope1.total,
            power?.ghg?.scope2.total,
        ]);
        // include finance enabling scope1+2
        // "Scope 3" here has to mean the whole of scope 3, upstream (.scope3) AND downstream
        // (.scope3_downstream) -- these are two separate fields on GhgObjectData (see
        // getGhgObjectForScope), and a client whose only real scope-3 activity is downstream (e.g.
        // Client 1.a in the 2026-09-21 test file: 0 everywhere except Scope 3 Local Downstream)
        // was showing n/a here even though the page-level GHG summary correctly includes it
        // (createGhgSummary's s3total_downstream, fixed 2026-09-18) -- same bug, same fix, applied
        // to the per-client overview table this time (Ceren, 2026-09-21).
        const scope123 = sumNumbersWithUndefined([
            scope12,
            finance?.ghg?.scope1.total,
            finance?.ghg?.scope2.total,
            finance?.ghg?.scope3.total,
            finance?.ghg?.scope3_downstream.total,
            temp?.ghg?.scope3.total,
            temp?.ghg?.scope3_downstream.total,
            perm?.ghg?.scope3.total,
            perm?.ghg?.scope3_downstream.total,
            power?.ghg?.scope3.total,
            power?.ghg?.scope3_downstream.total,
        ]);

        const ghgIntensity = !finance && attributionFilter !== "noFilter" ? intensityOf(scope12, outstandingAmount) : undefined;

        const employmentTotal = sumNumbersWithUndefined([
            temp?.employment?.combined.total,
            perm?.employment?.combined.total,
            finance?.employment?.combined.total,
            power?.employment?.combined.total,
        ]);

        const employmentIntensity =
            !finance && attributionFilter !== "noFilter" ? intensityOf(employmentTotal, outstandingAmount) : undefined;

        const valueAddedTotal = sumNumbersWithUndefined([
            temp?.valueAdded?.combined.netIncome,
            temp?.valueAdded?.combined.wages,
            temp?.valueAdded?.combined.taxes,
            perm?.valueAdded?.combined.netIncome,
            perm?.valueAdded?.combined.wages,
            perm?.valueAdded?.combined.taxes,
            finance?.valueAdded?.combined.netIncome,
            finance?.valueAdded?.combined.wages,
            finance?.valueAdded?.combined.taxes,
            power?.valueAdded?.combined.netIncome,
            power?.valueAdded?.combined.wages,
            power?.valueAdded?.combined.taxes,
        ]);

        const valueAddedIntensity =
            !finance && attributionFilter !== "noFilter" ? intensityOf(valueAddedTotal, outstandingAmount) : undefined;

        return [
            temp?.client || perm?.client || finance?.client || power?.client || "<Unknown>",
            temp?.country || perm?.country || finance?.country || power?.country || "<Unknown>",
            temp?.econAct || perm?.econAct || finance?.econAct || power?.econAct || "<Unknown>",
            //outstandingAmount if not finance enabling or investee
            investee || finance ? undefined : outstandingAmount,
            scope12,
            scope123,
            ghgIntensity,
            employmentTotal,
            employmentIntensity,
            valueAddedTotal,
            valueAddedIntensity,
        ];
    });
    return {
        columns: exampleData.overviewTableData.columns,
        data: combinedObjects,
    };
};

const createGhgTableData = (objects: GhgObjectData[], attributionFilter: AttributionFilterOption | null): SingleTableData => {
    return {
        // expected column order (one value per column of exampleData.ghgTableData, mirroring the output Table's
        // K, N, S, V-S, W, X, Y, Z): TextKey, TotalAdvances musd, Scope1 em, Scope2 em, Scope3 upstream em,
        // Scope3 downstream em, Scope 1+2 em, Scope 1+2+3 em (incl. downstream), Scope 1+2 intensity,
        // Scope 1+2+3 intensity
        columns: exampleData.ghgTableData.columns,
        data: objects.map((obj) => {
            // exception for financeenabling scope3 = scope1+2+3 and no scope 1 and 2
            const isFinance = obj.scope === Scope.FinanceEnabling;
            const scope1 = isFinance ? undefined : obj.scope1.total;
            const scope2 = isFinance ? undefined : obj.scope2.total;
            const scope3 = isFinance ? undefined : obj.scope3.total;
            // A finance-enabling row's own scope1/2/3 move into this "downstream" bucket for display, but its
            // REAL scope3_downstream.total (e.g. an SE position's own downstream data) must still be added,
            // not dropped -- otherwise the portfolio
            // total silently loses that mass and no longer reconciles with the Table sheet (which sums every
            // real sub-indicator value with no finance-enabling substitution). 2026-09-18.
            const scope3_downstream = isFinance
                ? sumNumbersWithUndefined([obj.scope1.total, obj.scope2.total, obj.scope3.total, obj.scope3_downstream.total])
                : obj.scope3_downstream.total;
            // Scope 3 in two columns: upstream (imports + local upstream, the output Table's column S) and
            // downstream (its V - S). Each has its own column -- the row once emitted both against ONE Scope 3
            // column, so every later value sat one column to the left.
            const scope12 = sumNumbersWithUndefined([scope1, scope2]);
            const scope123 = sumNumbersWithUndefined([scope12, scope3, scope3_downstream]);
            const intensity12 = attributionFilter !== "noFilter" ? intensityOf(scope12, obj.outstandingAmount) : undefined;
            const intensity123 = attributionFilter !== "noFilter" ? intensityOf(scope123, obj.outstandingAmount) : undefined;
            // report outstandingAmount only if not investee and not finance enabling
            const outstandingAmount = obj.investee || isFinance ? undefined : obj.outstandingAmount;
            return [obj.client, obj.country, obj.econAct, outstandingAmount, scope1, scope2, scope3, scope3_downstream, scope12, scope123, intensity12, intensity123];
        }),
    };
};

const createEmploymentTableData = (objects: EmploymentObjectData[], attributionFilter: AttributionFilterOption | null): SingleTableData => {
    return {
        // expected column order: TextKey, TotalAdvances musd, Direct [Total, Female%], Supply [Total, Female%, Formal%],
        //                        Induced [Total, Female%, Formal%], PowerEnabling [Total], Employment intensities
        columns: exampleData.employmentTableData.columns,
        data: objects.map((obj) => {
            return [
                obj.client,
                obj.country,
                obj.econAct,
                // report outstandingAmount only if not investee and not finance enabling
                obj.investee || obj.scope === Scope.FinanceEnabling || obj.scope === Scope.PowerEnabling ? undefined : obj.outstandingAmount,
                // in the direct, supply and induced only show backwards temp or perm data
                obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.direct.total : undefined,
                obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.supply.total : undefined,
                obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.induced.total : undefined,
                // sum together direct, supply and induced for finance and power enabling
                obj.scope === Scope.PowerEnabling ? obj.combined.total : undefined,
                obj.scope === Scope.FinanceEnabling ? obj.combined.total : undefined,
                attributionFilter !== "noFilter" ? intensityOf(obj.combined.total, obj.outstandingAmount) : undefined,
            ];
        }),
    };
};

const createValueAddedTableData = (objects: ValueAddedObjectData[], attributionFilter: AttributionFilterOption | null): SingleTableData => {
    return {
        // expected column order: TextKey, TotalAdvances musd, Direct [NetIncome, Taxes, Wages, Total], Supply [NetIncome, Taxes, Wages, Total], Economic intensities
        columns: exampleData.valueAddedTableData.columns,
        data: objects.map((obj) => {
            const directTotal = sumNumbersWithUndefined([obj.direct.netIncome, obj.direct.wages, obj.direct.taxes]);
            const supplyTotal = sumNumbersWithUndefined([obj.supply.netIncome, obj.supply.wages, obj.supply.taxes]);
            const combinedTotal = sumNumbersWithUndefined([directTotal, supplyTotal]);
            return [
                obj.client,
                obj.country,
                obj.econAct,
                // report outstandingAmount only if not investee and not finance enabling
                obj.investee || obj.scope === Scope.FinanceEnabling || obj.scope === Scope.PowerEnabling ? undefined : obj.outstandingAmount,
                obj.direct.netIncome,
                obj.direct.taxes,
                obj.direct.wages,
                directTotal,
                obj.supply.netIncome,
                obj.supply.taxes,
                obj.supply.wages,
                supplyTotal,
                // currently does not include power enabling, not sure if needed?
                attributionFilter !== "noFilter" ? intensityOf(combinedTotal, obj.outstandingAmount) : undefined,
            ];
        }),
    };
};

// create data for summaries

export const createInvestmentData = (generalObjects: GeneralObjectData[]): InvestmentDataType => {
    const clientCount = new Set(generalObjects.map((obj) => obj.client)).size;
    const countryCount = new Set(generalObjects.map((obj) => obj.country)).size;
    const revenueSum = sumNumbersWithUndefined(generalObjects.map((obj) => obj.revenue));
    // prevent multi counting of outstaning for clients with FI or Investees
    const outstandingSum = sumNumbersWithUndefined(
        generalObjects.map((obj) => (obj.investee || obj.scope === Scope.FinanceEnabling ? undefined : obj.outstandingAmount)),
    );
    return {
        clients: clientCount,
        countries: countryCount,
        totalRevenue: revenueSum,
        totalOutstanding: outstandingSum,
    };
};

const createGhgSummary = (objects: GhgObjectData[], attributionFilter: AttributionFilterOption | null): Summary => {
    // Expects two columns: Total amount (#) and intensity
    // for rows: Scope1, Scope2, Scope3
    // exception for financeenabling scope3 = scope1+2+3 and no scope 1 and 2
    const s1total = sumNumbersWithUndefined(objects.map((obj) => (obj.scope !== Scope.FinanceEnabling ? obj.scope1.total : undefined)));
    const s2total = sumNumbersWithUndefined(objects.map((obj) => (obj.scope !== Scope.FinanceEnabling ? obj.scope2.total : undefined)));
    const s3total = sumNumbersWithUndefined(objects.map((obj) => (obj.scope !== Scope.FinanceEnabling ? obj.scope3.total : undefined)));
    // Same fix as createGhgTableData above: a finance-enabling row's REAL scope3_downstream.total must be
    // added on top of its own scope1+2+3 substitution, not replaced by it -- otherwise this portfolio-level
    // summary silently drops that mass and diverges from the Table sheet total. 2026-09-18.
    const s3total_downstream = sumNumbersWithUndefined(
        objects.map((obj) =>
            obj.scope !== Scope.FinanceEnabling
                ? obj.scope3_downstream.total
                : sumNumbersWithUndefined([obj.scope1.total, obj.scope2.total, obj.scope3.total, obj.scope3_downstream.total]),
        ),
    );

    const scopesTotal = sumNumbersWithUndefined([s1total, s2total, s3total, s3total_downstream]);
    const outstandingTotal = sumNumbersWithUndefined(
        objects.filter((obj) => obj.scope !== Scope.FinanceEnabling && !obj.investee).map((obj) => obj.outstandingAmount),
    );
    return {
        // these strings are translated in the UI element, only change if you update translation
        columns: ["emissionTotal", "emissionIntensity"],
        data: [
            {
                title: "scope1",
                values: [s1total, attributionFilter !== "noFilter" ? intensityOf(s1total, outstandingTotal) : undefined],
            },
            {
                title: "scope2",
                values: [s2total, attributionFilter !== "noFilter" ? intensityOf(s2total, outstandingTotal) : undefined],
            },
            {
                title: "scope3 upstream",
                values: [s3total, attributionFilter !== "noFilter" ? intensityOf(s3total, outstandingTotal) : undefined],
            },
            {
                title: "scope3 downstream",
                values: [s3total_downstream, attributionFilter !== "noFilter" ? intensityOf(s3total_downstream, outstandingTotal) : undefined],
            },
        ],
        columnTotals: [scopesTotal, attributionFilter !== "noFilter" ? intensityOf(scopesTotal, outstandingTotal) : undefined],
    };
};

const createEmploymentSummary = (objects: EmploymentObjectData[], attributionFilter: AttributionFilterOption | null) => {
    // Expects two columns: Total amount (#) and intensity
    // for rows: direct, induced, supplyChain, financeEnabling, powerEnabling
    // Direct, supply chain and induced come from the Backward scopes only; Finance enabling and Power
    // enabling are their own rows (each the sum of its direct, supply chain and induced jobs).
    const direct = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.direct.total : undefined)),
    );
    const induced = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.induced.total : undefined)),
    );
    const supplyChain = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.supply.total : undefined)),
    );
    const financeEnabling = sumNumbersWithUndefined(objects.map((obj) => (obj.scope === Scope.FinanceEnabling ? obj.combined.total : undefined)));
    const powerEnabling = sumNumbersWithUndefined(objects.map((obj) => (obj.scope === Scope.PowerEnabling ? obj.combined.total : undefined)));
    const employmentTotal = sumNumbersWithUndefined([direct, financeEnabling, induced, powerEnabling, supplyChain]);
    const outstandingTotal = sumNumbersWithUndefined(
        objects
            .filter((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary) && !obj.investee)
            .map((obj) => obj.outstandingAmount),
    );
    return {
        // these strings are translated in the UI element, only change if you update translation
        columns: ["employmentTotal", "employmentIntensity"],
        data: [
            {
                title: "direct",
                values: [direct, attributionFilter !== "noFilter" ? intensityOf(direct, outstandingTotal) : undefined],
            },
            {
                title: "induced",
                values: [induced, attributionFilter !== "noFilter" ? intensityOf(induced, outstandingTotal) : undefined],
            },
            {
                title: "supplyChain",
                values: [supplyChain, attributionFilter !== "noFilter" ? intensityOf(supplyChain, outstandingTotal) : undefined],
            },
            {
                title: "financeEnabling",
                values: [financeEnabling, attributionFilter !== "noFilter" ? intensityOf(financeEnabling, outstandingTotal) : undefined],
            },
            {
                title: "powerEnabling",
                values: [powerEnabling, attributionFilter !== "noFilter" ? intensityOf(powerEnabling, outstandingTotal) : undefined],
            },
        ],
        columnTotals: [employmentTotal, attributionFilter !== "noFilter" ? intensityOf(employmentTotal, outstandingTotal) : undefined],
    };
};

const createValueAddedSummary = (objects: ValueAddedObjectData[], attributionFilter: AttributionFilterOption | null): Summary => {
    // Expects two columns: Total amount (#) and intensity
    // for rows: Net Income, Taxes, Wages
    const netIncome = sumNumbersWithUndefined(objects.map((obj) => sumNumbersWithUndefined([obj.direct.netIncome, obj.supply.netIncome, obj.power.netIncome])));
    const wages = sumNumbersWithUndefined(objects.map((obj) => sumNumbersWithUndefined([obj.direct.wages, obj.supply.wages, obj.power.wages])));
    const taxes = sumNumbersWithUndefined(objects.map((obj) => sumNumbersWithUndefined([obj.direct.taxes, obj.supply.taxes, obj.power.taxes])));
    const valueAddedTotal = sumNumbersWithUndefined([netIncome, wages, taxes]);
    // Loans from the Backward rows only, as in the jobs summary: a client with power enabling also has a
    // Power enabling object carrying the same loan, which was counted a second time here (halving the
    // value added intensity of such a portfolio; since at least 4.2).
    const outstandingTotal = sumNumbersWithUndefined(
        objects
            .filter((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary) && !obj.investee)
            .map((obj) => obj.outstandingAmount),
    );
    return {
        // these strings are translated in the UI element, only change if you update translation
        columns: ["economicTotal", "economicIntensity"],
        data: [
            {
                title: "wages",
                values: [wages, attributionFilter !== "noFilter" ? intensityOf(wages, outstandingTotal) : undefined],
            },
            {
                title: "netIncome",
                values: [netIncome, attributionFilter !== "noFilter" ? intensityOf(netIncome, outstandingTotal) : undefined],
            },
            {
                title: "taxes",
                values: [taxes, attributionFilter !== "noFilter" ? intensityOf(taxes, outstandingTotal) : undefined],
            },
        ],
        columnTotals: [valueAddedTotal, attributionFilter !== "noFilter" ? intensityOf(valueAddedTotal, outstandingTotal) : undefined],
    };
};

// create data for charts and tables per page

const createGhgIndividualClientsChartData = (objects: GhgObjectData[]): SummaryWithTotals => {
    // for rows: Scope1 [co2 / nonco2], Scope2 [co2 / nonco2], Scope3 [co2 / nonco2]
    const nonFinanceRows = objects.filter((obj) => obj.scope !== Scope.FinanceEnabling);

    const s1co2 = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope1.co2));
    const s2co2 = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope2.co2));
    const s3co2 = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope3.co2));
    const s3co2_downstream = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope3_downstream.co2));
    const s1nonco2 = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope1.nonco2));
    const s2nonco2 = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope2.nonco2));
    const s3nonco2 = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope3.nonco2));
    const s3nonco2_downstream = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.scope3_downstream.nonco2));
    return {
        columns: ["co2", "nonCo2"],
        data: [
            { title: "scope1", values: [s1co2, s1nonco2], total: sumNumbersWithUndefined([s1co2, s1nonco2]) },
            { title: "scope2", values: [s2co2, s2nonco2], total: sumNumbersWithUndefined([s2co2, s2nonco2]) },
            { title: "scope3 upstream", values: [s3co2, s3nonco2], total: sumNumbersWithUndefined([s3co2, s3nonco2]) },
            {
                title: "scope3 downstream",
                values: [s3co2_downstream, s3nonco2_downstream],
                total: sumNumbersWithUndefined([s3co2_downstream, s3nonco2_downstream]),
            },
        ],
        columnTotals: [
            sumNumbersWithUndefined([s1co2, s2co2, s3co2, s3co2_downstream]),
            sumNumbersWithUndefined([s1nonco2, s2nonco2, s3nonco2, s3nonco2_downstream]),
        ],
        total: sumNumbersWithUndefined([s1co2, s2co2, s3co2, s1nonco2, s2nonco2, s3nonco2, s3co2_downstream, s3nonco2_downstream]),
    };
};

const createGhgSectoralExposureChartData = (objects: GhgObjectData[]): SummaryWithTotals => {
    // should ONLY use Finance Enabling!
    const financeRows = objects.filter((obj) => obj.scope === Scope.FinanceEnabling);

    const s1co2 = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope1.co2));
    const s2co2 = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope2.co2));
    const s3co2 = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope3.co2));
    const s3co2_downstream = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope3_downstream.co2));
    const s1nonco2 = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope1.nonco2));
    const s2nonco2 = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope2.nonco2));
    const s3nonco2 = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope3.nonco2));
    const s3nonco2_downstream = sumNumbersWithUndefined(financeRows.map((obj) => obj.scope3_downstream.nonco2));
    return {
        columns: ["co2", "nonCo2"],
        data: [
            { title: "scope1", values: [s1co2, s1nonco2], total: sumNumbersWithUndefined([s1co2, s1nonco2]) },
            { title: "scope2", values: [s2co2, s2nonco2], total: sumNumbersWithUndefined([s2co2, s2nonco2]) },
            { title: "scope3 upstream", values: [s3co2, s3nonco2], total: sumNumbersWithUndefined([s3co2, s3nonco2]) },
            {
                title: "scope3 downstream",
                values: [s3co2_downstream, s3nonco2_downstream],
                total: sumNumbersWithUndefined([s3co2_downstream, s3nonco2_downstream]),
            },
        ],
        columnTotals: [
            sumNumbersWithUndefined([s1co2, s2co2, s3co2, s3co2_downstream]),
            sumNumbersWithUndefined([s1nonco2, s2nonco2, s3nonco2, s3nonco2_downstream]),
        ],
        total: sumNumbersWithUndefined([s1co2, s2co2, s3co2, s1nonco2, s2nonco2, s3nonco2, s3co2_downstream, s3nonco2_downstream]),
    };
};

const createEmploymentIndividualClientsTableData = (objects: EmploymentObjectData[]): SummaryWithTotals => {
    const directTotal = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.direct.total : undefined)),
    );
    const directFemale = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.direct.female : undefined)),
    );
    const directFormal = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.direct.formal : undefined)),
    );
    const inducedTotal = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.induced.total : undefined)),
    );
    const inducedFemale = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.induced.female : undefined)),
    );
    const inducedFormal = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.induced.formal : undefined)),
    );
    const supplyTotal = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.supply.total : undefined)),
    );
    const supplyFemale = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.supply.female : undefined)),
    );
    const supplyFormal = sumNumbersWithUndefined(
        objects.map((obj) => (obj.scope === Scope.BackwardPermanent || obj.scope === Scope.BackwardTemporary ? obj.supply.formal : undefined)),
    );
    const powerTotal = sumNumbersWithUndefined(objects.map((obj) => (obj.scope === Scope.PowerEnabling ? obj.combined.total : undefined)));
    const powerFemale = sumNumbersWithUndefined(objects.map((obj) => (obj.scope === Scope.PowerEnabling ? obj.combined.female : undefined)));
    const powerFormal = sumNumbersWithUndefined(objects.map((obj) => (obj.scope === Scope.PowerEnabling ? obj.combined.formal : undefined)));

    return {
        columns: ["ofWhichFemale", "ofWhichFormal"],
        data: [
            { title: "direct", values: [directFemale, directFormal], total: directTotal },
            { title: "induced", values: [inducedFemale, inducedFormal], total: inducedTotal },
            { title: "supplyChain", values: [supplyFemale, supplyFormal], total: supplyTotal },
            { title: "powerEnabling", values: [powerFemale, powerFormal], total: powerTotal },
        ],
        columnTotals: [
            sumNumbersWithUndefined([directFemale, inducedFemale, supplyFemale, powerFemale]),
            sumNumbersWithUndefined([directFormal, inducedFormal, supplyFormal, powerFormal]),
        ],
        total: sumNumbersWithUndefined([directTotal, inducedTotal, supplyTotal, powerTotal]),
    };
};

const createEmploymentSectoralExposureTableData = (objects: EmploymentObjectData[]): SummaryWithTotals => {
    // only take finance enabling here
    const financeRows = objects.filter((obj) => obj.scope === Scope.FinanceEnabling);

    const directTotal = sumNumbersWithUndefined(financeRows.map((obj) => obj.direct.total));
    const directFemale = sumNumbersWithUndefined(financeRows.map((obj) => obj.direct.female));
    const directFormal = sumNumbersWithUndefined(financeRows.map((obj) => obj.direct.formal));
    const inducedTotal = sumNumbersWithUndefined(financeRows.map((obj) => obj.induced.total));
    const inducedFemale = sumNumbersWithUndefined(financeRows.map((obj) => obj.induced.female));
    const inducedFormal = sumNumbersWithUndefined(financeRows.map((obj) => obj.induced.formal));
    const supplyTotal = sumNumbersWithUndefined(financeRows.map((obj) => obj.supply.total));
    const supplyFemale = sumNumbersWithUndefined(financeRows.map((obj) => obj.supply.female));
    const supplyFormal = sumNumbersWithUndefined(financeRows.map((obj) => obj.supply.formal));

    return {
        columns: ["ofWhichFemale", "ofWhichFormal"],
        data: [
            { title: "direct", values: [directFemale, directFormal], total: directTotal },
            { title: "induced", values: [inducedFemale, inducedFormal], total: inducedTotal },
            { title: "supplyChain", values: [supplyFemale, supplyFormal], total: supplyTotal },
        ],
        columnTotals: [
            sumNumbersWithUndefined([directFemale, inducedFemale, supplyFemale]),
            sumNumbersWithUndefined([directFormal, inducedFormal, supplyFormal]),
        ],
        total: sumNumbersWithUndefined([directTotal, inducedTotal, supplyTotal]),
    };
};

const createEmploymentIndividualClientsChartData = (summary: SummaryWithTotals): SummaryWithTotals => {
    const direct = summary.data.find((d) => d.title === "direct")?.total ?? undefined;
    const induced = summary.data.find((d) => d.title === "induced")?.total ?? undefined;
    const supplyChain = summary.data.find((d) => d.title === "supplyChain")?.total ?? undefined;
    const powerEnabling = summary.data.find((d) => d.title === "powerEnabling")?.total ?? undefined;
    const total = sumNumbersWithUndefined([direct, induced, supplyChain, powerEnabling]);
    return {
        columns: ["clients"],
        data: [
            { title: "direct", values: [direct], total: direct },
            { title: "induced", values: [induced], total: induced },
            { title: "supplyChain", values: [supplyChain], total: supplyChain },
            { title: "powerEnabling", values: [powerEnabling], total: powerEnabling },
        ],
        columnTotals: [total],
        total: total,
    };
};

const createEmploymentSectoralExposureChartData = (summary: SummaryWithTotals): SummaryWithTotals => {
    const direct = summary.data.find((d) => d.title === "direct")?.total ?? undefined;
    const induced = summary.data.find((d) => d.title === "induced")?.total ?? undefined;
    const supplyChain = summary.data.find((d) => d.title === "supplyChain")?.total ?? undefined;
    const total = sumNumbersWithUndefined([direct, induced, supplyChain]);
    return {
        columns: ["clients"],
        data: [
            { title: "direct", values: [direct], total: direct },
            { title: "induced", values: [induced], total: induced },
            { title: "supplyChain", values: [supplyChain], total: supplyChain },
        ],
        columnTotals: [total],
        total: total,
    };
};

const createValueAddedIndividualClientsTableData = (objects: ValueAddedObjectData[]): SummaryWithTotals => {
    const nonFinanceRows = objects.filter((obj) => obj.scope !== Scope.FinanceEnabling);
    const directWages = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.direct.wages));
    const directNetIncome = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.direct.netIncome));
    const directTaxes = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.direct.taxes));
    const supplyWages = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.supply.wages));
    const supplyNetIncome = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.supply.netIncome));
    const supplyTaxes = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.supply.taxes));
    const powerWages = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.power.wages));
    const powerNetIncome = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.power.netIncome));
    const powerTaxes = sumNumbersWithUndefined(nonFinanceRows.map((obj) => obj.power.taxes));

    return {
        columns: ["wages", "netIncome", "taxes"],
        data: [
            {
                title: "direct",
                values: [directWages, directNetIncome, directTaxes],
                total: sumNumbersWithUndefined([directWages, directNetIncome, directTaxes]),
            },
            {
                title: "supplyChain",
                values: [supplyWages, supplyNetIncome, supplyTaxes],
                total: sumNumbersWithUndefined([supplyWages, supplyNetIncome, supplyTaxes]),
            },
            {
                title: "powerEnabling",
                values: [powerWages, powerNetIncome, powerTaxes],
                total: sumNumbersWithUndefined([powerWages, powerNetIncome, powerTaxes]),
            },
        ],
        columnTotals: [
            sumNumbersWithUndefined([directWages, supplyWages, powerWages]),
            sumNumbersWithUndefined([directNetIncome, supplyNetIncome, powerNetIncome]),
            sumNumbersWithUndefined([directTaxes, supplyTaxes, powerTaxes]),
        ],
        total: sumNumbersWithUndefined([
            directWages,
            supplyWages,
            powerWages,
            directNetIncome,
            supplyNetIncome,
            powerNetIncome,
            directTaxes,
            supplyTaxes,
            powerTaxes,
        ]),
    };
};

const createValueAddedSectoralExposureTableData = (objects: ValueAddedObjectData[]): SummaryWithTotals => {
    // only take objects for financeEnabling
    const financeRows = objects.filter((obj) => obj.scope === Scope.FinanceEnabling);

    const directWages = sumNumbersWithUndefined(financeRows.map((obj) => obj.direct.wages));
    const directNetIncome = sumNumbersWithUndefined(financeRows.map((obj) => obj.direct.netIncome));
    const directTaxes = sumNumbersWithUndefined(financeRows.map((obj) => obj.direct.taxes));
    const supplyWages = sumNumbersWithUndefined(financeRows.map((obj) => obj.supply.wages));
    const supplyNetIncome = sumNumbersWithUndefined(financeRows.map((obj) => obj.supply.netIncome));
    const supplyTaxes = sumNumbersWithUndefined(financeRows.map((obj) => obj.supply.taxes));

    return {
        columns: ["wages", "netIncome", "taxes"],
        data: [
            {
                title: "direct",
                values: [directWages, directNetIncome, directTaxes],
                total: sumNumbersWithUndefined([directWages, directNetIncome, directTaxes]),
            },
            {
                title: "supplyChain",
                values: [supplyWages, supplyNetIncome, supplyTaxes],
                total: sumNumbersWithUndefined([supplyWages, supplyNetIncome, supplyTaxes]),
            },
        ],
        columnTotals: [
            sumNumbersWithUndefined([directWages, supplyWages]),
            sumNumbersWithUndefined([directNetIncome, supplyNetIncome]),
            sumNumbersWithUndefined([directTaxes, supplyTaxes]),
        ],
        total: sumNumbersWithUndefined([directWages, supplyWages, directNetIncome, supplyNetIncome, directTaxes, supplyTaxes]),
    };
};

// TODO remove at some point, made category specific implementations as the data is broken down differently
export const createSummary = (
    rawData: ExcelJsonSheet,
    columns: [keyof ExcelJsonRow, string][],
    rows: [string, string][],
    filterColumn: keyof ExcelJsonRow,
): Summary => {
    const resultColumns = columns.map((cp) => cp[1]);

    const data = rows.map((rowMapping) => {
        return {
            title: rowMapping[1],
            values: columns.map((columnPair) => {
                return rawData
                    .filter((row) => row[filterColumn] === rowMapping[0])
                    .reduce((previous, current) => previous + (current[columnPair[0]] as number), 0);
            }),
        };
    });
    const columnTotals = data.reduce(
        (p, c) => {
            const result = [...p];
            for (let i = 0; i < c.values.length; ++i) {
                result[i] += c.values[i];
            }
            return result;
        },
        columns.map(() => 0),
    );
    return { data, columns: resultColumns, columnTotals };
};

export const createTableAndChartData = (objectData: ObjectData, filter: Filter): Data | null => {
    // first apply general filter, also multiply with share if committed or outstanding is selected
    const filteredData = filterData(objectData, filter);

    // big tables at the bottom
    const ghgTableData = createGhgTableData(filteredData.ghg, filter.attribution);
    const employmentTableData = createEmploymentTableData(filteredData.employment, filter.attribution);
    const valueAddedTableData = createValueAddedTableData(filteredData.valueAdded, filter.attribution);
    const overviewTableData = createOverviewTableData(filteredData.ghg, filteredData.employment, filteredData.valueAdded, filter.attribution);

    // investmentData, used by filter and overview page
    const investmentData = createInvestmentData(filteredData.general);

    // summaries
    const ghgSummary = createGhgSummary(filteredData.ghg, filter.attribution);
    const employmentSummary = createEmploymentSummary(filteredData.employment, filter.attribution);
    const valueAddedSummary = createValueAddedSummary(filteredData.valueAdded, filter.attribution);

    // chart + tables
    const ghgIndividualClientsChartData = createGhgIndividualClientsChartData(filteredData.ghg);
    const ghgSectoralExposureChartData = createGhgSectoralExposureChartData(filteredData.ghg);

    const employmentIndividualClientsTableData = createEmploymentIndividualClientsTableData(filteredData.employment);
    const employmentSectoralExposureTableData = createEmploymentSectoralExposureTableData(filteredData.employment);
    // for employment the charts are a subset of the table data
    const employmentIndividualClientsChartData = createEmploymentIndividualClientsChartData(employmentIndividualClientsTableData);
    const employmentSectoralExposureChartData = createEmploymentSectoralExposureChartData(employmentSectoralExposureTableData);

    const valueAddedIndividualClientsChartData = createValueAddedIndividualClientsTableData(filteredData.valueAdded);
    const valueAddedSectoralExposureChartData = createValueAddedSectoralExposureTableData(filteredData.valueAdded);

    return {
        ghgTableData,
        employmentTableData,
        valueAddedTableData,
        overviewTableData,

        investmentData,
        ghgSummary,
        valueAddedSummary,
        employmentSummary,

        ghgIndividualClientsChartData,
        ghgSectoralExposureChartData,

        employmentIndividualClientsTableData,
        employmentSectoralExposureTableData,
        employmentIndividualClientsChartData,
        employmentSectoralExposureChartData,

        valueAddedIndividualClientsChartData,
        valueAddedSectoralExposureChartData,
    };
};
